import React, { useState, useEffect } from 'react';
import {
  Modal,
  Fade,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Avatar,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CakeRoundedIcon from '@mui/icons-material/CakeRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import RadioButtonUncheckedRoundedIcon from '@mui/icons-material/RadioButtonUncheckedRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

const GRADIENT = 'linear-gradient(135deg, #F97316 0%, #F59E0B 55%, #FBBF24 100%)';
const WHATSAPP_GREEN = '#25D366';

const substituirPlaceholder = (texto, nomePaciente) =>
  (texto || '').replace(/\{\{\s*paciente\s*\}\}/gi, nomePaciente || '');

const OpcaoMensagem = ({ selecionada, titulo, subtitulo, onClick, desabilitada }) => (
  <Box
    onClick={desabilitada ? undefined : onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.75,
      borderRadius: 3,
      border: '2px solid',
      borderColor: selecionada ? '#F97316' : 'divider',
      bgcolor: selecionada ? 'rgba(249,115,22,0.08)' : 'transparent',
      cursor: desabilitada ? 'default' : 'pointer',
      opacity: desabilitada ? 0.55 : 1,
      transition: 'all 0.15s ease',
      '&:hover': desabilitada ? {} : {
        borderColor: selecionada ? '#F97316' : 'rgba(249,115,22,0.4)',
        bgcolor: selecionada ? 'rgba(249,115,22,0.08)' : 'rgba(249,115,22,0.04)',
      },
    }}
  >
    {selecionada
      ? <CheckCircleRoundedIcon sx={{ color: '#F97316', fontSize: 22, flexShrink: 0 }} />
      : <RadioButtonUncheckedRoundedIcon sx={{ color: 'text.disabled', fontSize: 22, flexShrink: 0 }} />}
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{titulo}</Typography>
      {subtitulo && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{subtitulo}</Typography>}
    </Box>
  </Box>
);

const BirthdayMessageModal = ({ open, onClose, paciente, mensagemPadrao, mensagemPersonalizada, onSalvarMensagem }) => {
  const [tipo, setTipo] = useState(mensagemPersonalizada ? 'personalizada' : 'padrao');
  const [texto, setTexto] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [avisoSalvo, setAvisoSalvo] = useState(false);

  useEffect(() => {
    if (!open) return;
    const inicial = mensagemPersonalizada ? 'personalizada' : 'padrao';
    setTipo(inicial);
    setTexto(inicial === 'personalizada' ? mensagemPersonalizada : mensagemPadrao);
    setAvisoSalvo(false);
  }, [open, mensagemPadrao, mensagemPersonalizada]);

  const handleTipoChange = (novoTipo) => {
    setTipo(novoTipo);
    setTexto(novoTipo === 'personalizada' ? (mensagemPersonalizada || mensagemPadrao) : mensagemPadrao);
    setAvisoSalvo(false);
  };

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      await onSalvarMensagem(texto);
      setTipo('personalizada');
      setAvisoSalvo(true);
    } finally {
      setSalvando(false);
    }
  };

  const handleEnviar = () => {
    const textoFinal = substituirPlaceholder(texto, paciente?.nome);
    const numero = paciente?.telefone ? '55' + paciente.telefone.replace(/\D/g, '') : '';
    const url = `https://wa.me/${numero}?text=${encodeURIComponent(textoFinal)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  if (!paciente) return null;

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition>
      <Fade in={open}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: '0 24px 60px -12px rgba(0,0,0,0.35)',
          borderRadius: 4,
          width: '92%',
          maxWidth: 460,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}>
          {/* Header */}
          <Box sx={{
            position: 'relative',
            background: GRADIENT,
            px: 3, pt: 3, pb: 4,
            color: '#fff',
          }}>
            <IconButton
              onClick={onClose}
              size="small"
              sx={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.85)', bgcolor: 'rgba(255,255,255,0.12)', '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 44, height: 44, mb: 1.25 }}>
              <CakeRoundedIcon />
            </Avatar>
            <Typography sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.2 }}>
              Mensagem de aniversário
            </Typography>
            <Typography sx={{ fontSize: 13.5, opacity: 0.92, mt: 0.25 }}>
              Para {paciente.nome} 🎂
            </Typography>
          </Box>

          {/* Body */}
          <Box sx={{ p: 3, pt: 2.5, overflowY: 'auto' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, mb: 1 }}>
              Qual mensagem enviar?
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <OpcaoMensagem
                selecionada={tipo === 'padrao'}
                titulo="Mensagem padrão do sistema"
                onClick={() => handleTipoChange('padrao')}
              />
              <OpcaoMensagem
                selecionada={tipo === 'personalizada'}
                titulo="Minha mensagem personalizada"
                subtitulo={!mensagemPersonalizada ? 'Ainda não criada — edite abaixo e salve' : null}
                onClick={() => handleTipoChange('personalizada')}
              />
            </Box>

            <TextField
              multiline
              minRows={4}
              fullWidth
              value={texto}
              onChange={(e) => { setTexto(e.target.value); setAvisoSalvo(false); }}
              placeholder="Digite a mensagem..."
              sx={{
                mt: 2.5,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                  fontSize: 14,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#FAFAFA',
                },
              }}
            />
            <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              💡 Use <Box component="code" sx={{ bgcolor: 'action.selected', px: 0.6, py: 0.1, borderRadius: 1, fontSize: 11 }}>{'{{paciente}}'}</Box> para o nome entrar automaticamente.
            </Typography>

            <Fade in={avisoSalvo} unmountOnExit>
              <Box sx={{
                mt: 1.5, px: 1.5, py: 1, borderRadius: 2,
                bgcolor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)',
                display: 'flex', alignItems: 'center', gap: 1,
              }}>
                <CheckCircleRoundedIcon sx={{ color: '#22C55E', fontSize: 18 }} />
                <Typography sx={{ fontSize: 12.5, color: '#16A34A', fontWeight: 600 }}>
                  Mensagem personalizada salva!
                </Typography>
              </Box>
            </Fade>
          </Box>

          {/* Footer */}
          <Box sx={{
            display: 'flex', gap: 1, p: 2.5, pt: 1.5,
            borderTop: '1px solid', borderColor: 'divider',
          }}>
            <Button
              onClick={handleSalvar}
              disabled={salvando}
              startIcon={salvando ? <CircularProgress size={16} color="inherit" /> : <SaveRoundedIcon />}
              sx={{
                flex: 1,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 13,
                borderRadius: 2.5,
                py: 1.1,
                color: '#F97316',
                border: '1.5px solid rgba(249,115,22,0.4)',
                '&:hover': { bgcolor: 'rgba(249,115,22,0.08)', border: '1.5px solid #F97316' },
              }}
            >
              Salvar
            </Button>
            <Button
              onClick={handleEnviar}
              startIcon={<WhatsAppIcon />}
              sx={{
                flex: 1.4,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 13,
                borderRadius: 2.5,
                py: 1.1,
                color: '#fff',
                bgcolor: WHATSAPP_GREEN,
                boxShadow: '0 6px 16px -4px rgba(37,211,102,0.55)',
                '&:hover': { bgcolor: '#1FB855' },
              }}
            >
              Enviar no WhatsApp
            </Button>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default BirthdayMessageModal;
